"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = exports.ResizeStackedCTA = void 0;
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _styledResizeStackedCta = require("./styled-resize-stacked-cta");
var _excluded = ["parentContentWidth", "children", "className", "dataAnalyticsId", "dataTestId"];
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
var forwardRef = _react["default"].forwardRef,
  useEffect = _react["default"].useEffect,
  useRef = _react["default"].useRef;
var displayName = 'ResizeStackedCTA';
var propTypes = {
  parentContentWidth: _propTypes["default"].number.isRequired,
  children: _propTypes["default"].arrayOf(_propTypes["default"].node).isRequired,
  className: _propTypes["default"].string,
  dataAnalyticsId: _propTypes["default"].string,
  dataTestId: _propTypes["default"].string
};
var defaultProps = {
  className: '',
  dataAnalyticsId: '',
  dataTestId: ''
};

/**
 * Function to resize CTAs (button or link) when total CTA widths exceeds the parent content width by stacking CTA. The shorter CTA should grow to the same length as the longer CTA.
 * @param {Element} primaryCTA - This is the first button or link element to be stacked or resized
 * @param {Element} secondaryCTA - This is the second button or link element to be stacked or resized
 * @param {Element} ctaWrapper - This is the container or wrapper of the two buttons or links
 * @param {number} parentContentWidth - This is the reference width (could be sibling of ctaWrapper or parent content width of the ctaWrapper) which is used to calculate the stacking of buttons or link
 */
var resizeStackedButtons = function resizeStackedButtons(primaryCTA, secondaryCTA, ctaWrapper, parentContentWidth) {
  // Button gap set to initial side by side gap of --spacing-global-space6 = 16px
  var ctaGap = 16;
  var calcParentWidth = Math.floor(parentContentWidth) - 8; // Round down and adding some margins to account for button borders + extra spacing when total button widths calculation is close to copy wrapper width
  var primaryCTAClone = primaryCTA === null || primaryCTA === void 0 ? void 0 : primaryCTA.cloneNode(true);
  var secondaryCTAClone = secondaryCTA === null || secondaryCTA === void 0 ? void 0 : secondaryCTA.cloneNode(true);

  /**
   * Calculate actual widths of ctas by setting width to auto from 100% of container when stacked
   * Using cloned ctas for calculation removes chance of 'jank' as its not visible when appended and removed from DOM due to style set
   */
  Object.assign(primaryCTAClone.style, {
    width: 'auto',
    position: 'absolute',
    visibility: 'hidden'
  });
  Object.assign(secondaryCTAClone.style, {
    width: 'auto',
    position: 'absolute',
    visibility: 'hidden'
  });
  ctaWrapper === null || ctaWrapper === void 0 || ctaWrapper.appendChild(primaryCTAClone);
  ctaWrapper === null || ctaWrapper === void 0 || ctaWrapper.appendChild(secondaryCTAClone);

  // Get primary and secondary cta widths
  var primaryCTAWidth = primaryCTAClone === null || primaryCTAClone === void 0 ? void 0 : primaryCTAClone.offsetWidth;
  var secondaryCTAWidth = secondaryCTAClone === null || secondaryCTAClone === void 0 ? void 0 : secondaryCTAClone.offsetWidth;
  var totalCTAWidth = Math.ceil(primaryCTAWidth + ctaGap + secondaryCTAWidth); // Round up value so to mitigate edge cases

  ctaWrapper.removeChild(primaryCTAClone);
  ctaWrapper.removeChild(secondaryCTAClone);

  // Update cta styles to stacked or side by side based on total cta widths
  if (totalCTAWidth >= calcParentWidth && !ctaWrapper.classList.contains('fuse-utils-resize-stacked-ctas__ctas--stacked')) {
    ctaWrapper.classList.add('fuse-utils-resize-stacked-ctas__ctas--stacked');
    primaryCTA.classList.add('fuse-utils-resize-stacked-ctas__primary-cta--stacked');
    secondaryCTA.classList.add('fuse-utils-resize-stacked-ctas__secondary-cta--stacked');
  }
  if (totalCTAWidth < calcParentWidth && ctaWrapper.classList.contains('fuse-utils-resize-stacked-ctas__ctas--stacked')) {
    ctaWrapper.classList.remove('fuse-utils-resize-stacked-ctas__ctas--stacked');
    primaryCTA.classList.remove('fuse-utils-resize-stacked-ctas__primary-cta--stacked');
    secondaryCTA.classList.remove('fuse-utils-resize-stacked-ctas__secondary-cta--stacked');
  }
};
var ResizeStackedCTA = exports.ResizeStackedCTA = forwardRef(function (_ref, ref) {
  var parentContentWidth = _ref.parentContentWidth,
    children = _ref.children,
    className = _ref.className,
    dataAnalyticsId = _ref.dataAnalyticsId,
    dataTestId = _ref.dataTestId,
    others = _objectWithoutProperties(_ref, _excluded);
  // Refs
  var containerRef = useRef();
  var currentRef = ref || containerRef;

  /**
   * Resize ctas based on the parentContentWidth relative to ctas container
   */
  if (children.length === 2) {
    useEffect(function () {
      var ctas = currentRef.current.querySelectorAll('a, button');
      var primaryCTA = ctas === null || ctas === void 0 ? void 0 : ctas[0]; // First button
      var secondaryCTA = ctas === null || ctas === void 0 ? void 0 : ctas[1]; // Second button

      if (primaryCTA && secondaryCTA) resizeStackedButtons(primaryCTA, secondaryCTA, currentRef.current, parentContentWidth);
    }, [parentContentWidth]);
  }
  return /*#__PURE__*/_react["default"].createElement(_styledResizeStackedCta.StyledResizeStackedCTA, _extends({
    className: className,
    "data-analyticsid": dataAnalyticsId,
    "data-testid": dataTestId,
    ref: currentRef
  }, others), children);
});
ResizeStackedCTA.defaultProps = defaultProps;
ResizeStackedCTA.displayName = displayName;
ResizeStackedCTA.propTypes = propTypes;
var _default = exports["default"] = ResizeStackedCTA;